import { t } from 'i18next';
import { camelCase, isNull, omit, upperFirst } from 'lodash';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { AlternateVersionHelper } from 'core/helpers';
import { formatDate } from 'core/utils';
import { PriceCurrency, PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { externalSpaCostSourceFields } from 'quotes/constants';
import { QuotePartRecordType } from 'quotes/enums';
import { sanitizeAgreementText } from 'quotes/mappers/tradeAgreement';
import {
  ICustomerQuote,
  ICustomerQuotePart,
  IPricingSourceData,
  IQuotePart,
  IQuotePartCostData,
  IQuotePartPriceData,
  ITradeAgreement,
} from 'quotes/types';

export const formatPricingFieldValue = (
  key: string,
  value: unknown,
  costDecimals: number = PricingHelper.DEFAULT_COST_DECIMALS
): string => {
  if (/Date/.test(key)) {
    return formatDate(value as string);
  }

  if (/Cost$/.test(key)) {
    if (value == null) {
      return DEFAULT_PLACEHOLDER;
    }

    return PricingHelper.formatPricing({
      amount: Number(value),
      displayZero: true,
      decimals: costDecimals,
      fixedDecimals: true,
    });
  }

  return (value as string) ?? '';
};

export interface IPricingFieldMapping {
  field: string;
  mapTo?: string;
}

/**
 * External (pricing engine) fields survive a switch to another source, so the source type — not the
 * presence of data — is what says whether the engine's SPA/sell price is the one actually applied.
 */
export const isExternalPricingSourceInUse = (sourceType?: PricingSourceType): boolean =>
  PricingSourceType.EXTERNAL === sourceType;

export const isSelectedSpaSource = (sourceType?: PricingSourceType): boolean =>
  PricingSourceType.SELECTED_SPA === sourceType;

/**
 * The engine's SPA (EXTERNAL) or the user's pick (SELECTED_SPA). Cost side only.
 */
export const isSpaSourceInUse = (sourceType?: PricingSourceType): boolean =>
  isExternalPricingSourceInUse(sourceType) || isSelectedSpaSource(sourceType);

export interface ISelectedSpaDisplayField {
  labelKey: string;
  value: string;
}

const USD_CURRENCY_CODE = 'USD';

// AG-Grid hands the formatter a group/loading row with no data, so every getter tolerates null.
export const formatAgreementCost = (
  agreement?: ITradeAgreement | null,
  costDecimals?: number
): string => {
  if (agreement?.spaCost == null) {
    return DEFAULT_PLACEHOLDER;
  }

  // `currency` is numeral's prefix ('$'), not an ISO code: "USD" would silently drop the symbol.
  const isUsd = !agreement.spaCostCurrency || USD_CURRENCY_CODE === agreement.spaCostCurrency;

  const cost = PricingHelper.formatPricing({
    amount: Number(agreement.spaCost),
    currency: isUsd ? PriceCurrency.USD : '',
    decimals: costDecimals,
    displayZero: true,
    fixedDecimals: true,
  });

  const withCurrency = isUsd ? cost : `${cost} ${agreement.spaCostCurrency}`;

  return agreement.spaCostUom ? `${withCurrency} / ${agreement.spaCostUom}` : withCurrency;
};

export interface ISelectedSpaIdentity {
  spaCostRegistrationNum?: string;
  spaCostSource?: string;
}

type TSelectedSpaSource = Pick<IQuotePartCostData, 'costSourceType' | 'selectedSpa'> &
  Partial<
    Pick<
      ICustomerQuotePart,
      'computedCostSourceType' | 'costSourceSpaCostRegistrationNum' | 'costSourceSpaCostSource'
    >
  >;

/**
 * A grid row's computed type wins; the drawer's cost data carries only `costSourceType`.
 */
export const getCostSourceTypeInUse = (
  costSource?: TSelectedSpaSource
): PricingSourceType | undefined =>
  costSource?.computedCostSourceType ?? costSource?.costSourceType;

/**
 * Null for every other source type, so the agreement fields a converted line keeps never render.
 */
export const getSelectedSpaIdentity = (
  costSource?: TSelectedSpaSource
): ISelectedSpaIdentity | null => {
  if (!isSelectedSpaSource(getCostSourceTypeInUse(costSource))) {
    return null;
  }

  // A grid row's vendor text went through no mapper and renders through html-react-parser.
  const spaCostSource = sanitizeAgreementText(
    costSource?.selectedSpa?.spaCostSource ?? costSource?.costSourceSpaCostSource
  );
  const spaCostRegistrationNum = sanitizeAgreementText(
    costSource?.selectedSpa?.spaCostRegistrationNum ?? costSource?.costSourceSpaCostRegistrationNum
  );

  if (!spaCostSource && !spaCostRegistrationNum) {
    return null;
  }

  return { spaCostRegistrationNum, spaCostSource };
};

export interface ISelectedSpaPick {
  spaCostSource: string;
  spaCostSourceId: string | null;
}

/**
 * Code and version: another version of the same code is a new pick. An empty version is none.
 */
export const getSelectedSpaPick = (
  quotePart?: ICustomerQuotePart | null
): ISelectedSpaPick | null => {
  const spaCostSource = getSelectedSpaIdentity(quotePart)?.spaCostSource;

  return spaCostSource
    ? { spaCostSource, spaCostSourceId: quotePart?.costSourceSpaCostSourceId || null }
    : null;
};

type TEffectiveSpaSource = TSelectedSpaSource &
  Partial<
    Pick<
      ICustomerQuotePart,
      | 'costSourceSpaCostSourceName'
      | 'costSourceValidUntil'
      | 'externalSpaCost'
      | 'externalSpaCostEndDate'
      | 'externalSpaCostRegistrationNum'
      | 'externalSpaCostSource'
      | 'externalSpaCostSourceName'
      | 'purchaseCost'
    >
  >;

export interface IEffectiveSpaInfo {
  pricingData: IPricingSourceData;
  // Known only while the list entry is in the form; a saved pick stores none and reads as USD.
  spaCostCurrency?: string | null;
}

/**
 * The pick on a selected-SPA line, the engine's agreement otherwise, so both are presented alike.
 * A grid row's own purchase cost is the pick's cost.
 */
export const getEffectiveSpaInfo = (source?: TEffectiveSpaSource | null): IEffectiveSpaInfo => {
  if (!isSelectedSpaSource(getCostSourceTypeInUse(source))) {
    return {
      pricingData: pickPricingSourceFields(
        source as IQuotePartCostData,
        externalSpaCostSourceFields
      ),
    };
  }

  const pick = source?.selectedSpa;
  // Unmapped row text that lands in html-react-parser sinks.
  const spaInfo = {
    sourceType: PricingSourceType.SELECTED_SPA,
    externalSpaCostSource: sanitizeAgreementText(
      pick?.spaCostSource ?? source?.costSourceSpaCostSource
    ),
    externalSpaCostSourceName: sanitizeAgreementText(
      pick?.spaCostSourceName ?? source?.costSourceSpaCostSourceName
    ),
    externalSpaCostRegistrationNum: sanitizeAgreementText(
      pick?.spaCostRegistrationNum ?? source?.costSourceSpaCostRegistrationNum
    ),
    externalSpaCostEndDate: pick?.spaCostEndDate ?? source?.costSourceValidUntil,
    externalSpaCost: pick?.spaCost ?? source?.purchaseCost,
  };

  return {
    pricingData: pickPricingSourceFields(spaInfo as IQuotePartCostData, [
      { field: 'sourceType' },
      ...externalSpaCostSourceFields.filter(({ mapTo }) => !mapTo),
    ]),
    spaCostCurrency: pick?.spaCostCurrency,
  };
};

export const formatSpaCost = (
  value: unknown,
  costDecimals?: number,
  spaCostCurrency?: string | null
): string => {
  const isForeignCurrency = !!spaCostCurrency && USD_CURRENCY_CODE !== spaCostCurrency;

  return isForeignCurrency && value != null
    ? formatAgreementCost(
        { spaCost: Number(value), spaCostCurrency } as ITradeAgreement,
        costDecimals
      )
    : formatPricingFieldValue('externalSpaCost', value, costDecimals);
};

export const getPricingSourceDisplayFields = (
  pricingData?: IPricingSourceData | null,
  costDecimals?: number,
  {
    isShortLabel = false,
    spaCostCurrency,
  }: { isShortLabel?: boolean; spaCostCurrency?: string | null } = {}
): ISelectedSpaDisplayField[] => {
  const displayData = omit(pricingData ?? {}, 'sourceType');

  return Object.keys(displayData).map((key) => ({
    labelKey: `label${upperFirst(key)}${isShortLabel ? 'Short' : ''}`,
    value:
      'externalSpaCost' === key
        ? formatSpaCost(displayData[key], costDecimals, spaCostCurrency)
        : formatPricingFieldValue(key, displayData[key], costDecimals),
  }));
};

/**
 * Per-line availability is the response's `isSpaSelectionAvailable` flag, not this.
 */
export const isSelectedSpaSupported = ({
  quote,
  quotePart,
}: {
  quote?: ICustomerQuote | null;
  quotePart?: IQuotePart | null;
}): boolean => AlternateVersionHelper.isAlternateVersionEnabled({ quote, quotePart });

const selectSpaBlockedRecordTypes: QuotePartRecordType[] = [
  QuotePartRecordType.PART_DISCOUNT,
  QuotePartRecordType.PRICING_GROUP_DISCOUNT,
];

/**
 * Mirrors the BE eligibility checks, so the action is never offered where the save is refused.
 */
export const isSelectSpaBlockedForQuotePart = (quotePart?: ICustomerQuotePart | null): boolean =>
  Boolean(
    quotePart?.locked ||
      quotePart?.externalCostLocked ||
      quotePart?.substituted ||
      quotePart?.omitted ||
      (quotePart?.recordType && selectSpaBlockedRecordTypes.includes(quotePart.recordType))
  );

/**
 * A row carries a TINYINT (maybe a string), /quote/part/prices a boolean. `null` is unknown and
 * must never read as true.
 */
export const parseSpaSelectionAvailable = (value: unknown): boolean | null =>
  null == value ? null : 1 === Number(value);

/**
 * A held pick counts as flagged: the pricing answer to a call directed at a pick reports the flag
 * false. The local rules only hide it where the save would be refused.
 * `isAlternateVersionEnabled` must be the flag the save mappers gate the pick on.
 */
export const isSelectSpaActionAvailable = ({
  hasPart,
  hasSelectedSpa,
  isAlternateVersionEnabled,
  isSpaSelectionAvailable,
  quotePart,
}: {
  hasPart?: boolean;
  hasSelectedSpa?: boolean;
  isAlternateVersionEnabled?: boolean;
  isSpaSelectionAvailable?: boolean | null;
  quotePart?: ICustomerQuotePart | null;
}): boolean =>
  Boolean(isAlternateVersionEnabled) &&
  Boolean(hasPart) &&
  (Boolean(hasSelectedSpa) || true === isSpaSelectionAvailable) &&
  !isSelectSpaBlockedForQuotePart(quotePart);

/**
 * Ignores the engine flag, so a pick never outlives the offer with no way back.
 */
export const isRemoveSelectedSpaActionAvailable = ({
  hasPart,
  hasSelectedSpa,
  isAlternateVersionEnabled,
  quotePart,
}: {
  hasPart?: boolean;
  hasSelectedSpa?: boolean;
  isAlternateVersionEnabled?: boolean;
  quotePart?: ICustomerQuotePart | null;
}): boolean =>
  Boolean(isAlternateVersionEnabled) &&
  Boolean(hasPart) &&
  Boolean(hasSelectedSpa) &&
  !isSelectSpaBlockedForQuotePart(quotePart);

export const getActiveQuoteParts = (quoteParts: IQuotePart[]) =>
  quoteParts.filter((quotePart) => !quotePart?.deleted);

export const getPurchaseCostLabel = (isAlternateVersionEnabled: boolean) =>
  isAlternateVersionEnabled ? t('labelQuotedCost') : t('labelPurchaseCost');

export const isPnbPricingEnabled = (quotePart: ICustomerQuotePart) => {
  return (
    1 === +quotePart?.corporatePartConfig?.pnbPricingEnabled ||
    PricingHelper.isNonEmpty(getPurchaseCostBase(quotePart)) ||
    PricingHelper.isNonEmpty(getPurchaseCostAdder(quotePart)) ||
    PricingHelper.isNonEmpty(getCostBase(quotePart)) ||
    PricingHelper.isNonEmpty(getCostAdder(quotePart)) ||
    PricingHelper.isNonEmpty(getPriceBase(quotePart)) ||
    PricingHelper.isNonEmpty(getPriceAdder(quotePart))
  );
};

export const isBaseAdderPnbPricingEnabled = (quotePart: ICustomerQuotePart) => {
  return !isNull(quotePart?.partArr?.baseIdentifier) && isPnbPricingEnabled(quotePart);
};

export const isCostPnbFixed = (quotePart: ICustomerQuotePart) => {
  return (
    PricingSourceType.FAMILY === quotePart.computedCostSourceType &&
    PricingHelper.isEmpty(getPurchaseCostBase(quotePart)) &&
    PricingHelper.isEmpty(getPurchaseCostAdder(quotePart)) &&
    PricingHelper.isEmpty(getCostBase(quotePart)) &&
    PricingHelper.isEmpty(getCostAdder(quotePart))
  );
};

const COST_TYPE = 'cost';
const PURCHASE_COST_TYPE = 'purchaseCost';

const getPurchaseCostBase = (quotePart: ICustomerQuotePart): number | null =>
  getCostingFieldValue(quotePart, PURCHASE_COST_TYPE, PricingHelper.PRICING_TYPE_BASE);

const getPurchaseCostAdder = (quotePart: ICustomerQuotePart): number | null =>
  getCostingFieldValue(quotePart, PURCHASE_COST_TYPE, PricingHelper.PRICING_TYPE_ADDER);

const getCostBase = (quotePart: ICustomerQuotePart): number | null =>
  getCostingFieldValue(quotePart, COST_TYPE, PricingHelper.PRICING_TYPE_BASE);

const getCostAdder = (quotePart: ICustomerQuotePart): number | null =>
  getCostingFieldValue(quotePart, COST_TYPE, PricingHelper.PRICING_TYPE_ADDER);

const getPriceBase = (quotePart: ICustomerQuotePart): number | null =>
  getPrice(quotePart, PricingHelper.PRICING_TYPE_BASE);

const getPriceAdder = (quotePart: ICustomerQuotePart): number | null =>
  getPrice(quotePart, PricingHelper.PRICING_TYPE_ADDER);

/**
 * @param  {string}  fieldType   E.g "cost", "purchaseCost".
 * @param  {string}  costingType E.g. "total", "base", "adder".
 * @return {?number}             Costing field value as float, or null if
 *                               empty.
 */
const getCostingFieldValue = (
  quotePart: ICustomerQuotePart,
  fieldType: string,
  costingType: string
): number => PricingHelper.get(quotePart?.[getCostingFieldName(fieldType, costingType)]);

/**
 * @param  {string} fieldType   E.g "cost", "purchaseCost".
 * @param  {string} costingType E.g. "total", "base", "adder".
 * @return {string}
 */
const getCostingFieldName = (fieldType: string, costingType: string): string => {
  let fieldName = 'computed_';

  switch (costingType) {
    case PricingHelper.PRICING_TYPE_TOTAL:
      fieldName += fieldType;
      break;
    case PricingHelper.PRICING_TYPE_BASE:
    case PricingHelper.PRICING_TYPE_ADDER:
      fieldName += `${fieldType}${upperFirst(costingType)}`;
      break;
    default:
      throw new Error(`Invalid costingType: "${costingType}".`);
  }

  return camelCase(fieldName);
};

const getPrice = (quotePart: ICustomerQuotePart, type: string): number | null => {
  let field = 'price';

  if (PricingHelper.PRICING_TYPE_TOTAL !== type) {
    field += upperFirst(type);
  }

  return PricingHelper.get(quotePart?.[camelCase(field)]);
};

export const pickPricingSourceFields = (
  data: IQuotePartCostData | IQuotePartPriceData,
  fieldMappings: IPricingFieldMapping[]
): IPricingSourceData =>
  fieldMappings.reduce((acc, { field, mapTo }) => {
    const key = mapTo || field;
    const value = data?.[field as keyof typeof data];

    if (value != null) {
      acc[key] = value;
    }

    return acc;
  }, {} as IPricingSourceData);
